/**
 * Availability, defaults and input rules for the monogram configurator. The
 * config is the JSON written by snippet.monogram-trigger.liquid.
 */

export const GOLD_FOIL = "goldFoil";
export const HAND_PAINTED = "handPainted";
export const SIZES = [1, 2];
export const DEFAULT_PLACEMENT_SCALE = 50;

// Prices in the store's currency.
export const GOLD_FOIL_PRICE = 50;
export const GOLD_FOIL_REDUCED_PRICE = 30;
export const GOLD_FOIL_REDUCED_MAX_PRODUCT_PRICE = 95;
export const HAND_PAINTED_FIRST_LETTER_PRICE = 50;
export const HAND_PAINTED_ADDITIONAL_LETTER_PRICE = 25;

/**
 * Placements offering the given type — a placement opts out of a type by
 * leaving its max letters at 0.
 */
export function placementsFor(config, type) {
  const placements = config.placements ?? [];

  if (type === GOLD_FOIL) {
    return placements.filter((placement) => placement.goldFoilMax > 0);
  }

  return placements.filter((placement) => sizesFor(placement).length > 0);
}

/** Hand painted sizes (in inches) a placement allows. */
export function sizesFor(placement) {
  if (!placement) return [];

  return SIZES.filter(
    (size) => maxLettersFor(placement, HAND_PAINTED, size) > 0,
  );
}

export function maxLettersFor(placement, type, size) {
  if (!placement) return 0;
  if (type === GOLD_FOIL) return placement.goldFoilMax || 0;

  return (
    (size === 2 ? placement.handPainted2Max : placement.handPainted1Max) || 0
  );
}

export function availableTypes(config) {
  const types = [];
  const goldFoil = config.goldFoil;
  const handPainted = config.handPainted;

  if (goldFoil?.fonts?.length && placementsFor(config, GOLD_FOIL).length) {
    types.push(GOLD_FOIL);
  }

  if (
    handPainted?.font?.url &&
    handPainted.colors?.length &&
    placementsFor(config, HAND_PAINTED).length
  ) {
    types.push(HAND_PAINTED);
  }

  return types;
}

/**
 * A percentage scale field as a multiplier (100 → 1), or the fallback when
 * blank.
 *
 * @param {number|string|null|undefined} value
 * @param {number} fallback - Percentage used when the field is blank
 */
export function scaleOf(value, fallback) {
  const number = parseFloat(value);
  return (Number.isFinite(number) ? number : fallback) / 100;
}

/** A font's scale as a multiplier; a blank scale counts as 100. */
export function fontScale(font) {
  return scaleOf(font?.scale, 100);
}

/** Gold foil fonts offered for a selection; phrases have their own. */
export function fontsFor(config, selection) {
  if (selection.type !== GOLD_FOIL) return [];

  return (
    (selection.entry === "phrase"
      ? config.goldFoil?.phraseFonts
      : config.goldFoil?.fonts) ?? []
  );
}

export function fontFor(config, selection) {
  if (selection.type === HAND_PAINTED) return config.handPainted?.font ?? null;

  const fonts = fontsFor(config, selection);
  return fonts.find((font) => font.id === selection.fontId) ?? fonts[0] ?? null;
}

export function placementFor(config, selection) {
  const placements = placementsFor(config, selection.type);

  return (
    placements.find((placement) => placement.id === selection.placementId) ??
    placements[0] ??
    null
  );
}

/**
 * Whether a gold foil placement offers phrases: it has a single position, and
 * there are phrases with fonts to set them in.
 */
function offersPhrases(config, placement) {
  return (
    (config.goldFoil?.phrases?.length ?? 0) > 0 &&
    (config.goldFoil?.phraseFonts?.length ?? 0) > 0 &&
    positionsOf(placement).length <= 1
  );
}

/** Phrases offered for a selection. */
export function phrasesFor(config, selection) {
  if (selection.type !== GOLD_FOIL) return [];
  if (!offersPhrases(config, placementFor(config, selection))) return [];

  return config.goldFoil.phrases;
}

/** Where a placement's letters go: its own position, then its additional ones. */
export function positionsOf(placement) {
  return placement?.positions ?? [];
}

/**
 * Splits text across positions as evenly as it goes, earlier positions taking
 * the extra letters: "ABCDE" across 3 is AB, CD, E; "A" across 2 is A, "".
 *
 * @param {string} text
 * @param {number} count
 * @returns {string[]}
 */
export function splitText(text, count) {
  const base = Math.floor(text.length / count);
  const extra = text.length % count;
  const parts = [];
  let start = 0;

  for (let index = 0; index < count; index++) {
    const length = base + (index < extra ? 1 : 0);
    parts.push(text.slice(start, start + length));
    start += length;
  }

  return parts;
}

/**
 * The image edges a position's percentages are measured from. A centre anchor
 * measures from the top or left.
 *
 * @returns {{ x: "left"|"right", y: "top"|"bottom" }}
 */
export function anchorEdges(position) {
  return {
    x: position?.horizontalAnchor === "right" ? "right" : "left",
    y: position?.anchor === "bottom" ? "bottom" : "top",
  };
}

/**
 * Steps shown for a type, in order. A step is judged on the type's whole
 * option set rather than the current selection, so numbering holds while
 * options change — the size step stays even when the selected placement
 * allows a single size, and the font step even when phrases have one font.
 */
export function visibleSteps(config, type) {
  const placements = placementsFor(config, type);
  const steps = ["text"];

  if (type === GOLD_FOIL) {
    const phraseFonts = placements.some((placement) =>
      offersPhrases(config, placement),
    )
      ? config.goldFoil.phraseFonts.length
      : 0;
    const textFonts = config.goldFoil?.fonts?.length ?? 0;
    if (Math.max(textFonts, phraseFonts) > 1) steps.push("font");
  } else {
    if (placements.some((placement) => sizesFor(placement).length > 1)) {
      steps.push("size");
    }

    const colors = config.handPainted?.colors?.length ?? 0;
    if (colors > 1) steps.push("color");
  }

  if (placements.length > 1) steps.push("placement");

  return steps;
}

/**
 * Uppercases and strips anything the font can't take, then cuts to length.
 *
 * @param {string} text
 * @param {{ numbers: boolean, max: number }} rules
 */
export function sanitizeText(text, { numbers, max }) {
  const allowed = numbers ? /[A-Z0-9]/ : /[A-Z]/;

  return [...(text ?? "").toUpperCase()]
    .filter((character) => allowed.test(character))
    .join("")
    .slice(0, max);
}

export function textRules(config, selection) {
  const font = fontFor(config, selection);
  const placement = placementFor(config, selection);

  return {
    numbers: Boolean(font?.numbersAvailable),
    max: maxLettersFor(placement, selection.type, selection.size),
  };
}

/**
 * Fills defaults and pulls every choice back to a valid one: each falls back
 * to the first available option, and the text is re-cut to the current rules.
 */
export function normalizeSelection(config, selection = {}) {
  const types = availableTypes(config);
  const type = types.includes(selection.type) ? selection.type : types[0];
  const next = { ...selection, type };

  next.placementId = placementFor(config, next)?.id ?? null;

  if (type !== GOLD_FOIL) {
    const sizes = sizesFor(placementFor(config, next));
    next.size = sizes.includes(selection.size) ? selection.size : sizes[0];

    const colors = config.handPainted?.colors ?? [];
    const colorIds = colors.map((color) => color.id);
    next.firstColorId = colorIds.includes(selection.firstColorId)
      ? selection.firstColorId
      : colorIds[0] ?? null;

    // Second colour is the drop shadow; null is "None", the default.
    next.secondColorId =
      colorIds.includes(selection.secondColorId) &&
      selection.secondColorId !== next.firstColorId
        ? selection.secondColorId
        : null;
  }

  const phrases = phrasesFor(config, next);
  next.entry =
    selection.entry === "phrase" && phrases.length ? "phrase" : "text";
  next.phrase = phrases.includes(selection.phrase)
    ? selection.phrase
    : phrases[0];

  // After the entry, which decides the fonts on offer.
  if (type === GOLD_FOIL) next.fontId = fontFor(config, next)?.id ?? null;

  next.text = sanitizeText(selection.text, textRules(config, next));

  return next;
}

/** What the preview shows and the monogram is made from. */
export function monogramText(selection) {
  return selection.entry === "phrase" ? selection.phrase ?? "" : selection.text;
}

/**
 * Gold foil's price in the store's currency, which drops for a product at or
 * under GOLD_FOIL_REDUCED_MAX_PRODUCT_PRICE.
 *
 * @param {number} productPrice - In cents, in the shopper's currency
 * @param {number} [rate] - The shopper's currency per unit of the store's
 */
export function goldFoilPrice(productPrice, rate = 1) {
  return productPrice / 100 <= GOLD_FOIL_REDUCED_MAX_PRODUCT_PRICE * rate
    ? GOLD_FOIL_REDUCED_PRICE
    : GOLD_FOIL_PRICE;
}

/** Hand painting's price in the store's currency, by letter count. */
export function handPaintedPrice(letters) {
  if (!letters) return 0;

  return (
    HAND_PAINTED_FIRST_LETTER_PRICE +
    HAND_PAINTED_ADDITIONAL_LETTER_PRICE * (letters - 1)
  );
}

/**
 * A selection's price in the store's currency.
 *
 * @param {number} productPrice - In cents, in the shopper's currency
 * @param {number} [rate] - The shopper's currency per unit of the store's
 */
export function monogramPrice(selection, productPrice, rate = 1) {
  return selection.type === GOLD_FOIL
    ? goldFoilPrice(productPrice, rate)
    : handPaintedPrice(monogramText(selection).length);
}

/**
 * The type's pricing product variant for a price: the one whose option value
 * is that price in the store's currency.
 */
export function pricingVariant(config, type, price) {
  return (
    (config.pricing?.[type] ?? []).find(
      (variant) => Number(variant.value) === price,
    ) ?? null
  );
}
